import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { CarrierIssueType } from "@freightsimple/generated-carriers-openapi-client/models/CarrierIssueType";
import { type ShipmentDetail } from "@freightsimple/generated-carriers-openapi-client/models/ShipmentDetail";
import { type ShipmentInvoiceDetail } from "@freightsimple/generated-carriers-openapi-client/models/ShipmentInvoiceDetail";
import { IssueBanner } from "@/App/Shipment/IssueBanner";
import { FlagBanner } from "@/App/Shipment/FlagBanner";
import { AuditBanner } from "@/App/Shipment/AuditBanner";

// Most urgent first: freight that is lost or late is a service failure, a
// booking still needs doing, and a customs hold is only a notice.
const ISSUE_ORDER: CarrierIssueType[] = [
  CarrierIssueType.MissingFreight,
  CarrierIssueType.MissedPickup,
  CarrierIssueType.MissedDelivery,
  CarrierIssueType.ExpectedDeliveryIsInThePast,
  CarrierIssueType.TrackingNotUpdated,
  CarrierIssueType.BookPickup,
  CarrierIssueType.PendingCustomsClearance,
];

/**
 * The work surface at the top of a shipment page.
 *
 * Open operational issues render here, each carrying its response action. This
 * is why the shipment page exists: the issues list is only an index, and a
 * carrier following a link from an email lands here and answers in place.
 *
 * Freight-condition flags sit above the issues. Freight that is lost, damaged or
 * short is the outcome those tasks exist to prevent, so it outranks them.
 *
 * The audit banner comes last. Freight is moving and issues are time-critical,
 * whereas an audit is money already spent.
 */
export function ShipmentBanners({
  shipment,
  invoice,
  onResponded,
  onSeeWhy,
}: {
  shipment: ShipmentDetail;
  invoice: ShipmentInvoiceDetail | undefined;
  onResponded: () => void;
  onSeeWhy: () => void;
}) {
  const issues = [...(shipment.openIssues ?? [])].sort(
    (a, b) =>
      ISSUE_ORDER.indexOf(a.issueType) - ISSUE_ORDER.indexOf(b.issueType),
  );
  const flags = shipment.flags ?? [];
  const auditOpen = invoice?.auditOpen === true;
  if (issues.length === 0 && flags.length === 0 && !auditOpen) return undefined;

  return (
    <Stack gap={2}>
      {flags.map((flag) => (
        <FlagBanner key={flag.flagType} flag={flag} />
      ))}
      {issues.map((issue) => (
        <IssueBanner
          key={issue.issueType}
          issue={issue}
          onResponded={onResponded}
        />
      ))}
      {auditOpen && invoice && (
        <AuditBanner invoice={invoice} onSeeWhy={onSeeWhy} />
      )}
    </Stack>
  );
}
